import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import { alpha } from "@mui/material/styles";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "../../hooks";
function formatExpiry(date) {
    if (!date)
        return "";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime()))
        return date;
    return parsed.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}
export function WalletCard({ packs, }) {
    const { t } = useTranslation("portal");
    const [expanded, setExpanded] = React.useState(false);
    const active = (packs ?? []).filter((pack) => (pack.sessionsRemaining ?? 0) > 0);
    const balance = active.reduce((total, pack) => total + (pack.sessionsRemaining ?? 0), 0);
    if (balance <= 0)
        return null;
    const ordered = [...active].sort((a, b) => {
        const left = a.expiresOn ? Date.parse(a.expiresOn) : Number.MAX_SAFE_INTEGER;
        const right = b.expiresOn
            ? Date.parse(b.expiresOn)
            : Number.MAX_SAFE_INTEGER;
        return left - right;
    });
    return (_jsxs(Box, { sx: (theme) => ({
            position: "relative",
            overflow: "hidden",
            borderRadius: 2,
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            color: theme.palette.primary.contrastText,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 10px 30px -18px ${alpha(theme.palette.primary.main, 0.9)}`,
            "&::after": {
                content: '""',
                position: "absolute",
                top: -60,
                insetInlineEnd: -40,
                width: 160,
                height: 160,
                borderRadius: "50%",
                backgroundColor: alpha("#ffffff", 0.08),
                pointerEvents: "none",
            },
        }), children: [_jsxs(Stack, { direction: "row", spacing: 1.5, sx: { alignItems: "center" }, children: [_jsx(Box, { sx: {
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            flexShrink: 0,
                            backgroundColor: alpha("#ffffff", 0.16),
                            "& svg": { fontSize: 20 },
                        }, children: _jsx(AccountBalanceWalletRoundedIcon, {}) }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    opacity: 0.75,
                                }, children: t("walletTitle", "Session credits") }), _jsx(Typography, { sx: { fontWeight: 700, fontSize: { xs: 19, sm: 21 }, mt: 0.25 }, children: t("walletBalance", "{{count}} sessions available", {
                                    count: balance,
                                }) })] })] }), _jsx(Typography, { variant: "caption", sx: { display: "block", mt: 1, opacity: 0.75 }, children: t("walletShared", "Shared across everyone on this phone number.") }), _jsx(Button, { size: "small", onClick: () => setExpanded((open) => !open), "aria-expanded": expanded, endIcon: _jsx(ExpandMoreRoundedIcon, { sx: {
                        transition: "transform 160ms ease",
                        transform: expanded ? "rotate(180deg)" : "none",
                        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    } }), sx: {
                    mt: 1.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    fontSize: 12.5,
                    color: "inherit",
                    backgroundColor: alpha("#ffffff", 0.14),
                    "&:hover": { backgroundColor: alpha("#ffffff", 0.24) },
                }, children: t("walletBreakdown", "View breakdown") }), _jsx(Collapse, { in: expanded, unmountOnExit: true, children: _jsx(Stack, { spacing: 1.25, sx: {
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${alpha("#ffffff", 0.2)}`,
                    }, children: ordered.map((pack) => (_jsxs(Stack, { direction: "row", spacing: 2, sx: { justifyContent: "space-between", alignItems: "center" }, children: [_jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: pack.sessionPackName || "—" }), pack.expiresOn ? (_jsx(Typography, { variant: "caption", sx: { opacity: 0.75 }, children: t("walletExpires", "Expires {{date}}", {
                                            date: formatExpiry(pack.expiresOn),
                                        }) })) : null] }), _jsx(Box, { sx: {
                                    flexShrink: 0,
                                    px: 1.25,
                                    py: 0.375,
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    backgroundColor: alpha("#ffffff", 0.16),
                                }, children: t("walletRemaining", "{{count}} left", {
                                    count: pack.sessionsRemaining,
                                }) })] }, pack.id))) }) })] }));
}
